import { cartAtom, cartHashAtom, latestProductIdAtom } from '@/store';
import { useAtom, useSetAtom } from 'jotai';
import Button from 'components/src/button';
import Image from 'next/image';
import { formatISK, hashCart } from '@/utils/utils';
import { useRouter } from 'next/dist/client/components/navigation';
import { motion } from 'framer-motion';
import Close from 'components/src/svg/Close';
import ArrowRight from '@/components/Icons/ArrowRight';
import ImagePlaceholder from '@/components/common/ImagePlaceholder';

const NavbarAddedToCartBox = ({ onClose }: { onClose: () => void }) => {
  const [cart] = useAtom(cartAtom);
  const [latestProductId] = useAtom(latestProductIdAtom);
  const setCartHash = useSetAtom(cartHashAtom);
  const router = useRouter();
  const cartPath = '/vefverslun/karfa';

  const forward = async () => {
    const hash = await hashCart(cart);
    setCartHash(hash);
    router.push('/vefverslun/karfa/upplysingar');
  };

  const renderProductInfo = () => {
    const itemKey = latestProductId && cart[latestProductId] ? latestProductId : Object.keys(cart)[0];
    const item = cart[itemKey];

    if (!item) return null;

    return (
      <>
        <div key={itemKey} className="flex flex-[2] flex-col items-start justify-center gap-4 text-base">
          <h4 className="font-bold">{item.product.name}</h4>
          <span>{item.product.variant}</span>
          <span>{item.product.subvariant}</span>
          <span className="font-bold text-red-100">{formatISK(item.product.price)}</span>
        </div>
        <div className="flex h-[120px] w-[120px] items-center justify-center p-8">
          {item.product?.imageUrl ? (
            <Image
              alt={item.product?.imageUrl || ''}
              src={item.product?.imageUrl || ''}
              width={110}
              height={110}
              className="h-[110px] w-[110px] rounded-4 object-contain"
            />
          ) : (
            <div className="h-full w-full rounded-4 bg-white-5 p-24">
              <ImagePlaceholder bg="bg-white-5" />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <motion.div className="pointer-events-none col-span-12 mx-16 flex justify-end md:col-start-2 md:-mx-16">
      <motion.div
        initial={{ opacity: 0, y: -6, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.2 } }}
        transition={{ duration: 0.3 }}
        className="pointer-events-auto relative flex w-full flex-col justify-start gap-[10px] rounded-28 bg-white-100 p-16 sm:min-w-[320px] sm:max-w-[330px] "
        style={{
          boxShadow:
            '0 103px 29px 0 rgba(30, 30, 28, 0.00), 0 66px 26px 0 rgba(30, 30, 28, 0.01), 0 37px 22px 0 rgba(30, 30, 28, 0.03), 0 16px 16px 0 rgba(30, 30, 28, 0.10), 0 4px 9px 0 rgba(30, 30, 28, 0.10)',
          transformOrigin: 'top',
        }}
      >
        <motion.div
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.1, transition: { duration: 0.5 } }}
          className="absolute -right-8 -top-4 flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full border border-white-5 bg-white-100"
          onClick={onClose}
        >
          <Close size="16" color="#181818" />
        </motion.div>
        <h3 className="pb-2 pt-8 text-base font-bold"> Bætt í körfu </h3>
        <hr className="text-white-5"></hr>
        <div className="flex items-center gap-12">{renderProductInfo()}</div>

        <Button
          onClick={() => router.push(cartPath)}
          variant="surface"
          label="Fara í körfu"
          bordered
          iconPosition="right"
          autoWidth={false}
        />
        <Button
          onClick={forward}
          label="Klára kaup"
          bordered
          icon={<ArrowRight />}
          iconPosition="right"
          autoWidth={false}
        />
      </motion.div>
    </motion.div>
  );
};

export default NavbarAddedToCartBox;
